import { type PageType } from "modules/analytics/types"
import {
  type ComponentType,
  type ReactNode,
  useCallback,
  useContext,
  createContext,
  forwardRef,
} from "react"
import {
  type AnalyticsBaseEvent,
  type AnalyticsButtonEventProps,
  type AnalyticsButtonEventType,
  type AnalyticsChangeEventProps,
  type AnalyticsLinkEventProps,
  type GaButtonEventTrigger,
  type GaChangeEventTrigger,
  type GaLinkEventTrigger,
} from "utils/analytics"

/*
 * Use these tools to track sections/components where custom tracking is done based on click/input/some action. For simple form tracking, there are simpler methods if it's only submit etc.
 *
 * How to use:
 * 1. Wrap the part of code you wanna wrap within Tracking and give it an id
 * 2. Check that all components you wanna track already use useTracking or add it yourself
 * 3. If there's more detailed things, you can nest Tracking providers. See how ChartFooter uses "contextDetail"
 */

interface InitialData {
  context?: AnalyticsBaseEvent["context"]
  id?: string // Calling "section" id in this api to make it clearer that it should be unique per page like element id.
  pageType?: `${PageType}`
  pageId?: string
}

type EventData = Pick<AnalyticsBaseEvent, "name" | "context" | "contextDetail">

const TrackingContext = createContext<
  EventData & Partial<InitialData> & Record<string, any>
>({})

TrackingContext.displayName = "TrackingContext"

type TrackingProps = (
  | (InitialData & Record<string, any>)
  | (EventData & { id?: never; pageType?: never; pageId?: never })
) & {
  children: ReactNode
}
export const Tracking = ({ children, ...trackingData }: TrackingProps) => {
  const contextValue = useContext(TrackingContext)

  return (
    <TrackingContext.Provider value={{ ...contextValue, ...trackingData }}>
      {children}
    </TrackingContext.Provider>
  )
}

export const withTracking = <T extends Record<any, unknown>>(
  Component: ComponentType<any>,
  trackingProps: Omit<TrackingProps, "children">
) => {
  const TrackedComponent = forwardRef<HTMLElement, T>((props, ref) => (
    <Tracking {...trackingProps}>
      <Component {...props} ref={ref} />
    </Tracking>
  ))
  TrackedComponent.displayName = "TrackedComponent"

  return TrackedComponent
}

type GaEventProps =
  | AnalyticsButtonEventProps
  | AnalyticsLinkEventProps
  | AnalyticsChangeEventProps
type GaEventTrigger =
  | GaButtonEventTrigger
  | GaLinkEventTrigger
  | GaChangeEventTrigger

const isChangeTrigger = (
  trigger: GaEventTrigger
): trigger is GaChangeEventTrigger => trigger.length === 1

export function useTracking() {
  const contextValue = useContext(TrackingContext)

  const dispatch = useCallback(
    (
      gaEventTrigger: GaEventTrigger,
      eventPayload: GaEventProps,
      e?: AnalyticsButtonEventType
    ) => {
      /*
       * TODO: fix/improve types.
       * For change trigger event we dont pass event and we want to enforce that the "text" prop is passed right away.
       */
      if (contextValue.id) {
        const { id, ...rest } = contextValue
        if (isChangeTrigger(gaEventTrigger)) {
          gaEventTrigger({
            section: id,
            ...rest,
            ...eventPayload,
          } as AnalyticsChangeEventProps)
        } else if (e) {
          const payload: AnalyticsButtonEventProps | AnalyticsLinkEventProps = {
            section: id,
            ...rest,
            ...eventPayload,
          }
          gaEventTrigger(payload as any, e)
        }
      }
    },
    [contextValue]
  )

  return {
    contextValue,
    dispatch,
  }
}
